import React, { useEffect, useState } from 'react';
import { addOpsStaff, deleteOpsStaff, getOpsStaff } from '../../../api/twoDays/ops.api';
import { Field, ModalButton, Select, TextInput } from '../../../components/TwoDays/Modal';
import { JOBS, jobLabel } from '../../../components/TwoDays/ops/opsModel';
import { StatusChip } from '../../../components/TwoDays/primitives';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Staff (Ops) — 직무별 운영진 스태프 계정.
 * 태스크를 받을 직무의 팀원이 없으면(이탈 포함) 그 직무의 스태프가 신청·Pre-join 없이 팀원으로 들어가 받는다.
 * 같은 이메일의 코레디 계정으로 로그인하면 그 팀 워크보드를 볼 수 있다.
 */
const OpsStaff = () => {
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', job: 'planner' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getOpsStaff()
      .then(setStaff)
      .catch(() => setError('Couldn’t load staff accounts. Refresh the page.'));
  }, []);

  const ok = form.name.trim() && EMAIL_RE.test(form.email.trim());
  const add = async () => {
    setSaving(true);
    setError('');
    try {
      setStaff(await addOpsStaff({ ...form, name: form.name.trim(), email: form.email.trim() }));
      setForm((f) => ({ ...f, name: '', email: '' }));
    } catch (e) {
      setError(
        e.response?.status === 400
          ? 'This email is already registered.'
          : 'Couldn’t add. Try again.',
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    try {
      setStaff(await deleteOpsStaff(id));
    } catch {
      setError('Couldn’t remove. Try again.');
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
        Staff
      </h1>
      <section className='flex flex-col rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4 lg:max-w-[880px]'>
        <header className='border-b border-green-deep03/[0.08] px-5 py-6 sm:px-7'>
          <h2 className='text-[20px] font-medium leading-[130%] text-text-primary'>
            Staff accounts
          </h2>
          <p className='mt-1 text-[14px] leading-[150%] text-text-secondary'>
            When a team has no member for the receiving role, or a member drops, the staff account
            for that role joins the team and receives the task. No application or Pre-join needed.
          </p>
          <p className='mt-1 text-[13px] leading-[150%] text-text-secondary'>
            Adding a staff member creates a Koready account for that email (default password
            Test1234!). If the email already has an account, it’s linked as is.
          </p>
        </header>

        <div className='flex flex-col gap-6 px-5 py-6 sm:px-7'>
          <div className='grid gap-4 sm:grid-cols-[140px_1fr_1fr_auto] sm:items-end'>
            <Field label='Role' htmlFor='staff-job'>
              <Select
                id='staff-job'
                value={form.job}
                onChange={(e) => setForm((f) => ({ ...f, job: e.target.value }))}
              >
                {JOBS.map((j) => (
                  <option key={j.key} value={j.key}>
                    {j.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label='Name' htmlFor='staff-name'>
              <TextInput
                id='staff-name'
                placeholder='e.g. Koready Designer'
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field label='Koready account email' htmlFor='staff-email'>
              <TextInput
                id='staff-email'
                type='email'
                placeholder='designer@ko-ready.com'
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Field>
            <ModalButton primary disabled={!ok || saving} onClick={add}>
              Add
            </ModalButton>
          </div>

          {error && <p className='text-[13px] text-functional-failed04'>{error}</p>}

          {staff == null ? (
            <div className='h-24 animate-pulse rounded-xl bg-grayscale-gray01' />
          ) : staff.length === 0 ? (
            <p className='rounded-xl border border-dashed border-green-deep03/20 px-4 py-10 text-center text-[14px] text-text-secondary'>
              No staff accounts yet. Add one for each role.
            </p>
          ) : (
            <div className='overflow-x-auto rounded-xl border border-green-deep03/[0.08] shadow-4'>
              <table className='w-full min-w-[600px] text-left text-[14px] text-text-primary'>
                <thead className='bg-grayscale-gray01 text-[12px] font-medium text-text-secondary'>
                  <tr>
                    <th className='px-4 py-2.5 font-medium'>Role</th>
                    <th className='px-4 py-2.5 font-medium'>Name</th>
                    <th className='px-4 py-2.5 font-medium'>Email</th>
                    <th className='px-4 py-2.5 font-medium'>Account</th>
                    <th className='px-4 py-2.5' />
                  </tr>
                </thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.id} className='border-t border-green-deep03/[0.08]'>
                      <td className='px-4 py-3'>{jobLabel(s.job)}</td>
                      <td className='px-4 py-3 font-medium'>{s.name}</td>
                      <td className='px-4 py-3 text-text-secondary'>{s.email}</td>
                      <td className='px-4 py-3'>
                        <StatusChip
                          chip={
                            s.accountLinked
                              ? { label: 'Linked', tone: 'success' }
                              : { label: 'No account yet', tone: 'gray' }
                          }
                        />
                      </td>
                      <td className='px-4 py-3 text-right'>
                        <button
                          type='button'
                          onClick={() => remove(s.id)}
                          className='text-[12px] text-text-secondary hover:text-functional-failed04'
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default OpsStaff;
